import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, RotateCcw } from 'lucide-react';
import type { Series, Track } from '@pujolisique/shared';
import { fetchSeries, resetProgress, saveProgress, useSeries, useStories } from '../api/hooks';
import { queryClient } from '../queryClient';
import { Cover } from '../components/Cover';
import { PageHeader } from '../components/PageHeader';
import { Empty, ErrorBox, Loading } from '../components/States';
import { Bars } from '../components/TrackList';
import { DeleteButton, useTrackMenu } from '../components/trackActions';
import { currentTrack, usePlayer } from '../player/store';
import { fmtLong, fmtTime, plural } from '../lib/format';
import { toast } from '../ui/toast';
import { Bio } from './Detail';

function refresh() {
  queryClient.invalidateQueries({ queryKey: ['stories'] });
  queryClient.invalidateQueries({ queryKey: ['series'] });
  queryClient.invalidateQueries({ queryKey: ['home'] });
}

/** Lance une série à l'épisode donné (la reprise de position est gérée par le lecteur) */
export function usePlaySeries() {
  const playTracks = usePlayer((s) => s.playTracks);
  return async (seriesId: string, index?: number) => {
    try {
      const s = await fetchSeries(seriesId);
      if (!s.episodes.length) return;
      playTracks(s.episodes, index ?? s.resume?.index ?? 0, { shuffle: false, source: s.name });
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
}

function ProgressBar({ value, label }: { value: number; label?: string }) {
  return (
    <span className="pbar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value * 100)} aria-label={label}>
      <span style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }} />
    </span>
  );
}

export function SeriesCard({ series, showResume }: { series: Series; showResume?: boolean }) {
  const play = usePlaySeries();
  const done = series.finishedCount === series.episodeCount;
  const sub = showResume && series.resume
    ? `Épisode ${series.resume.index + 1}${series.resume.position > 0 ? `, à ${fmtTime(series.resume.position)}` : ''}`
    : done
      ? 'Terminée'
      : plural(series.episodeCount, 'épisode', 'épisodes');
  return (
    <Link to={`/stories/${series.id}`} className="card card--series">
      <div className="card__art">
        <Cover art={series.cover} name={series.name} size={256} />
        <button
          className="fab"
          aria-label={`${series.resume?.position ? 'Reprendre' : 'Écouter'} ${series.name}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void play(series.id);
          }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" fill="currentColor" />
          </svg>
        </button>
        {series.finishedCount > 0 && <ProgressBar value={series.finishedCount / series.episodeCount} label="Épisodes écoutés" />}
      </div>
      <span className="card__title">{series.name}</span>
      <span className="card__sub">{sub}</span>
    </Link>
  );
}

export function StoriesPage() {
  const q = useStories();
  const groups = useMemo(() => {
    const m = new Map<string, Series[]>();
    for (const s of q.data ?? []) m.set(s.parent ?? '', [...(m.get(s.parent ?? '') ?? []), s]);
    return [...m.entries()];
  }, [q.data]);
  const inProgress = (q.data ?? []).filter((s) => s.lastPlayedAt && s.finishedCount < s.episodeCount).sort((a, b) => (b.lastPlayedAt ?? 0) - (a.lastPlayedAt ?? 0));

  return (
    <div className="page">
      <h1 className="page__title">Histoires</h1>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && !q.data.length && <Empty title="Aucune histoire pour l’instant">Déposez des fichiers audio dans le dossier des histoires : un dossier par série ou par livre.</Empty>}
      {inProgress.length > 0 && (
        <section className="section section--first">
          <h2 className="section__title">Reprendre</h2>
          <div className="grid">
            {inProgress.slice(0, 6).map((s) => (
              <SeriesCard key={s.id} series={s} showResume />
            ))}
          </div>
        </section>
      )}
      {groups.map(([parent, list]) => (
        <section key={parent || '_'} className="section">
          <h2 className="section__title">{parent || (groups.length > 1 ? 'Autres histoires' : 'Toutes les histoires')}</h2>
          <div className="grid">
            {list.map((s) => (
              <SeriesCard key={s.id} series={s} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function SeriesPage() {
  const { id = '' } = useParams();
  const q = useSeries(id);
  const play = usePlaySeries();
  const current = usePlayer(currentTrack);
  const playing = usePlayer((s) => s.playing);
  const menu = useTrackMenu();
  const nav = useNavigate();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const s = q.data;
  const resume = s.resume;
  const started = s.episodes.some((e) => e.progress);

  const toggleDone = async (e: Track) => {
    try {
      if (e.progress?.finished) await resetProgress([e.id]);
      else await saveProgress(e.id, e.duration, true);
      refresh();
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <div className="page page--detail">
      <PageHeader
        kind={s.parent ? `Histoire, ${s.parent}` : 'Histoire'}
        title={s.name}
        art={s.cover}
        editPath={`/edit/series/${s.id}`}
        trailing={<DeleteButton scope={{ series: s.id }} label={`la série « ${s.name} »`} onDone={() => nav('/stories', { replace: true })} />}
        meta={
          <>
            {s.authors && s.authors.length > 0 && <span className="strong">{s.authors.join(', ')}</span>}
            {s.year && <span>{s.year}</span>}
            <span>{plural(s.episodeCount, 'épisode', 'épisodes')}</span>
            <span>{fmtLong(s.duration)}</span>
            {s.finishedCount > 0 && <span className="strong">{s.finishedCount === s.episodeCount ? 'Terminée' : `${s.finishedCount} écouté${s.finishedCount > 1 ? 's' : ''}`}</span>}
          </>
        }
        actions={
          <>
            <button className="btn btn--gold btn--big" onClick={() => void play(s.id)}>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" fill="currentColor" />
              </svg>
              {resume && started ? `Reprendre l’épisode ${resume.index + 1}${resume.position > 0 ? ` à ${fmtTime(resume.position)}` : ''}` : 'Écouter depuis le début'}
            </button>
            {started && (
              <button
                className="btn btn--ghost"
                onClick={async () => {
                  if (!confirm('Effacer la progression de toute la série ?')) return;
                  await resetProgress(s.episodes.map((e) => e.id));
                  refresh();
                  toast('Progression effacée');
                }}
              >
                <RotateCcw size={16} /> Recommencer
              </button>
            )}
          </>
        }
      />
      {s.description && <Bio text={s.description} />}
      <ol className="episodes">
        {s.episodes.map((e, i) => {
          const isCur = current?.id === e.id;
          const pos = e.progress && !e.progress.finished ? e.progress.position : 0;
          return (
            <li
              key={e.id}
              className={`episode ${isCur ? 'is-current' : ''} ${e.progress?.finished ? 'is-done' : ''}`}
              onContextMenu={(ev) => {
                ev.preventDefault();
                menu(e, ev);
              }}
            >
              <button className="episode__main" onClick={() => void play(s.id, i)} aria-label={`Écouter ${e.title}`}>
                <span className="episode__num">{isCur && playing ? <Bars /> : i + 1}</span>
                <span className="episode__text">
                  <span className="episode__title">{e.title}</span>
                  <span className="episode__sub">
                    {e.progress?.finished ? 'Écouté' : pos > 0 ? `Reste ${fmtTime(e.duration - pos)}` : fmtTime(e.duration)}
                  </span>
                  {pos > 0 && <ProgressBar value={pos / e.duration} label="Avancement de l’épisode" />}
                </span>
              </button>
              <button
                className={`icon-btn episode__done ${e.progress?.finished ? 'is-on' : ''}`}
                onClick={() => void toggleDone(e)}
                aria-pressed={!!e.progress?.finished}
                aria-label={e.progress?.finished ? 'Marquer comme non écouté' : 'Marquer comme écouté'}
                title={e.progress?.finished ? 'Marquer comme non écouté' : 'Marquer comme écouté'}
              >
                <Check size={18} />
              </button>
            </li>
          );
        })}
      </ol>
      {((s.genres?.length ?? 0) > 0 || (s.themes?.length ?? 0) > 0) && (
        <p className="foot-meta">
          {s.genres?.map((g) => (
            <Link key={g} to={`/search?q=${encodeURIComponent(g)}`} className="chip">
              {g}
            </Link>
          ))}
          {s.themes?.map((t) => (
            <Link key={`t-${t}`} to={`/search?q=${encodeURIComponent(t)}`} className="chip chip--theme" title="Thème">
              # {t}
            </Link>
          ))}
        </p>
      )}
    </div>
  );
}

export { ProgressBar };
